import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, DoorOpen, Users } from "lucide-react";
import api from "@/lib/api";
import { WEEKDAYS, sortByPeriod } from "@/components/Timetable";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const toMin = (t) => { const m = /^(\d{1,2}):(\d{2})$/.exec(t || ""); return m ? Number(m[1]) * 60 + Number(m[2]) : null; };
const fmtHour = (m) => { const h = Math.floor(m / 60); return `${((h + 11) % 12) + 1} ${h < 12 ? "AM" : "PM"}`; };
const mondayOf = (d) => { const x = new Date(d.getFullYear(), d.getMonth(), d.getDate()); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const PX_PER_MIN = 1.6;

/** Calendar-style week: hour rows, Mon–Fri columns with dates, periods as time blocks. */
export function ScheduleWeek({ classes, onSelect, testid = "schedule-week" }) {
  const [start, setStart] = useState(() => mondayOf(new Date()));
  const [offDays, setOffDays] = useState({});
  const [tick, setTick] = useState(Date.now());
  const days = useMemo(() => WEEKDAYS.map((k, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return { key: k, date: d, iso: iso(d) }; }), [start]);

  useEffect(() => {
    api.get("/calendar", { params: { from: days[0].iso, to: days[4].iso } })
      .then(({ data }) => setOffDays(Object.fromEntries(data.map((e) => [e.date, e]))))
      .catch(() => setOffDays({}));
  }, [days]);
  useEffect(() => { const iv = setInterval(() => setTick(Date.now()), 60000); return () => clearInterval(iv); }, []);

  const timed = classes.filter((c) => toMin(c.start_time) !== null && toMin(c.end_time) !== null);
  const untimed = sortByPeriod(classes.filter((c) => !timed.includes(c)));
  const rangeStart = Math.floor(Math.min(8 * 60, ...timed.map((c) => toMin(c.start_time))) / 60) * 60;
  const rangeEnd = Math.ceil(Math.max(15 * 60, ...timed.map((c) => toMin(c.end_time))) / 60) * 60;
  const hours = []; for (let m = rangeStart; m < rangeEnd; m += 60) hours.push(m);
  const gridH = (rangeEnd - rangeStart) * PX_PER_MIN;
  const todayIso = iso(new Date());
  const now = new Date(tick); const nowMin = now.getHours() * 60 + now.getMinutes();
  const title = `${days[0].date.toLocaleDateString([], { month: "short", day: "numeric" })} – ${days[4].date.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}`;
  const shift = (n) => { const d = new Date(start); d.setDate(d.getDate() + n * 7); setStart(d); };

  const Block = ({ c, top, height, dayIso }) => (
    <button type="button" onClick={() => onSelect?.(c)} data-testid={`${testid}-cell-${dayIso}-${c.id}`} style={{ top, height }}
      className="absolute inset-x-1 rounded-lg border border-primary/30 bg-primary/10 hover:bg-primary/15 hover:border-primary/60 px-2 py-1 text-left overflow-hidden transition">
      <div className="text-[10px] font-bold uppercase tracking-widest text-primary truncate">{c.period}{c.start_time ? <span className="ml-1 font-medium normal-case tracking-normal text-slate-500">{c.start_time}–{c.end_time}</span> : null}</div>
      <div className="text-sm font-semibold text-slate-900 leading-tight truncate">{c.name}</div>
      <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-600">
        {c.room && <span className="inline-flex items-center gap-0.5"><DoorOpen className="h-3 w-3" /> Rm {c.room}</span>}
        <span className="inline-flex items-center gap-0.5"><Users className="h-3 w-3" /> {c.student_count}</span>
      </div>
    </button>
  );

  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <button type="button" onClick={() => shift(-1)} data-testid={`${testid}-prev`} className="h-8 w-8 rounded-lg border border-slate-200 bg-white inline-flex items-center justify-center text-slate-600 hover:bg-slate-100"><ChevronLeft className="h-4 w-4" /></button>
        <div className="font-display font-semibold text-slate-900 min-w-56 text-center" data-testid={`${testid}-title`}>{title}</div>
        <button type="button" onClick={() => shift(1)} data-testid={`${testid}-next`} className="h-8 w-8 rounded-lg border border-slate-200 bg-white inline-flex items-center justify-center text-slate-600 hover:bg-slate-100"><ChevronRight className="h-4 w-4" /></button>
        <button type="button" onClick={() => setStart(mondayOf(new Date()))} data-testid={`${testid}-today`} className="ml-1 h-8 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 hover:bg-slate-100">Today</button>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="grid grid-cols-[64px_repeat(5,1fr)] border-b border-slate-200 bg-slate-50/60">
          <div />
          {days.map((d) => {
            const isToday = d.iso === todayIso;
            return (
              <div key={d.key} className="px-2 py-2 border-l border-slate-100 flex items-center gap-2" data-testid={`${testid}-head-${d.iso}`}>
                <span className={`text-[11px] font-bold uppercase tracking-widest ${isToday ? "text-primary" : "text-slate-500"}`}>{d.key}</span>
                <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${isToday ? "bg-primary text-white" : "text-slate-700"}`}>{d.date.getDate()}</span>
                {offDays[d.iso] && <span className="ml-auto rounded bg-rose-50 border border-rose-100 px-1.5 py-0.5 text-[10px] font-medium text-rose-700 truncate" data-testid={`${testid}-off-${d.iso}`}>{offDays[d.iso].label}</span>}
              </div>
            );
          })}
        </div>
        {untimed.length > 0 && (
          <div className="grid grid-cols-[64px_repeat(5,1fr)] border-b border-slate-200">
            <div className="px-2 py-2 text-[10px] font-semibold uppercase tracking-widest text-slate-400 text-right">No time</div>
            {days.map((d) => (
              <div key={d.key} className="border-l border-slate-100 p-1 space-y-1">
                {!offDays[d.iso] && untimed.filter((c) => (c.days || WEEKDAYS).includes(d.key)).map((c) => (
                  <button key={c.id} type="button" onClick={() => onSelect?.(c)} data-testid={`${testid}-cell-${d.iso}-${c.id}`}
                    className="w-full rounded-md border border-slate-200 bg-slate-50 hover:bg-slate-100 px-2 py-1 text-left text-xs truncate transition"><b className="text-primary">{c.period || "HR"}</b> {c.name}</button>
                ))}
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-[64px_repeat(5,1fr)]" style={{ height: gridH }}>
          <div className="relative">
            {hours.map((m) => <div key={m} className="absolute right-2 -translate-y-1/2 text-[11px] text-slate-400" style={{ top: (m - rangeStart) * PX_PER_MIN }}>{m === rangeStart ? "" : fmtHour(m)}</div>)}
          </div>
          {days.map((d) => {
            const off = offDays[d.iso];
            const list = off ? [] : timed.filter((c) => (c.days || WEEKDAYS).includes(d.key));
            const isToday = d.iso === todayIso;
            return (
              <div key={d.key} className={`relative border-l border-slate-100 ${off ? "bg-rose-50/40" : isToday ? "bg-primary/[0.03]" : ""}`} data-testid={`${testid}-col-${d.iso}`}>
                {hours.map((m) => <div key={m} className="absolute inset-x-0 border-t border-slate-100" style={{ top: (m - rangeStart) * PX_PER_MIN }} />)}
                {list.map((c) => <Block key={c.id} c={c} dayIso={d.iso} top={(toMin(c.start_time) - rangeStart) * PX_PER_MIN + 1} height={Math.max(28, (toMin(c.end_time) - toMin(c.start_time)) * PX_PER_MIN - 2)} />)}
                {isToday && nowMin >= rangeStart && nowMin <= rangeEnd && (
                  <div className="absolute inset-x-0 z-10 flex items-center pointer-events-none" style={{ top: (nowMin - rangeStart) * PX_PER_MIN }} data-testid={`${testid}-now`}>
                    <span className="h-2.5 w-2.5 -ml-1 rounded-full bg-rose-500" /><span className="flex-1 border-t-2 border-rose-500" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
